import React, { useEffect, useRef, useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import folderIcon from '../../../../_metronic/assets/images/svg/data-room/blue-folder-icon.svg'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { exportCSVAccessLogs, getAudits } from '../core/_requests'
import { toast } from 'react-toastify'
import { NoDataPage } from '../../resources/components/NoDataPage'
import { useAuth } from '../../auth'
import { DisplayImage } from '../../widgets/components/General/DisplayImage'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import { Filter } from '../../widgets/components/General/Filter'
import { getFileType } from '../../../core/_util'
import { useTimeZone } from '../../../hooks/useTimeZone'
import { useDateFormat } from '../../../hooks/useDateFormat'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

// interface AccessLogs {
//   file: string
//   role: string
//   ipAddress: string
//   id: number
//   name: string
//   title: string
//   email: string
//   phone: string
// }

export const AccessLogsTable = () => {
  const { companyId } = useAuth()
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const { formatMessage } = useIntl()
  const [isLoading, setLoading] = useState<boolean>(false)
  // const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [, setShowKebabMenu] = useState(false)
  const [search,] = useState('') //eslint-disable-line
  const { getTimeZoneValue } = useTimeZone()
  const { getDateValue } = useDateFormat()
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }
  const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const emailMatch = rankItem(row.original.email, value)
    const nameMatch = rankItem(`${row.original.documentName}`, value)

    // Determine which match to use based on rank comparison or other logic
    let bestMatch: RankingInfo | undefined
    if (emailMatch.passed && nameMatch.passed) {
      bestMatch = emailMatch.rank < nameMatch.rank ? emailMatch : nameMatch
    } else if (emailMatch.passed) {
      bestMatch = emailMatch
    } else if (nameMatch.passed) {
      bestMatch = nameMatch
    }

    // Only call addMeta if a best match is found
    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    // Return whether the search term passed in either field
    return !!bestMatch
  }
  useEffect(() => {
    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const [data, setData] = useState<any[]>([])
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })

  const getAllAudits = async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const {
        data: { data, success, errors },
      } = await getAudits(companyId, pagination.currentPage, pagination.pageSize, search)
      if (success) {
        setData(data?.data)
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination.currentPage,
          pageSize: data.pagination.pageSize,
          totalCount: data.pagination.totalRecords,
          nextPage: data.pagination.nextPage,
          prevPage: data.pagination.prevPage,
          totalPages: data.pagination.totalPages,
        }))
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAllAudits()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, pagination.currentPage, pagination.pageSize, search])

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        cell: (info) => (
          <div className='d-flex align-items-center gap-2'>
            <div className='position-relative'>
              <DisplayImage
                className='rounded-circle'
                imgName={info.row.original.profileImage}
                alt={'profile-pic'}
                height={32}
                width={32}
              />
              <div className='d-flex position-absolute' style={{ bottom: '0', right: '0' }}>
                <DisplayImage
                  className='rounded-circle'
                  imgName={info.row.original.profileImage}
                  alt={'profile-pic'}
                  height={12}
                  width={12}
                />
              </div>
            </div>
            <div>
              <h5 className='m-0 font-size-13 fw-normal'>{info.row.original.name}</h5>
              <p className='m-0 text-muted font-size-10'>{info.row.original.ipAddress}</p>
            </div>
          </div>
        ),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'email',
        id: 'email',
        header: 'Email & Role',
        cell: (info) => (
          <div className='d-flex align-items-center gap-2'>
            <div>
              <h5 className='m-0 font-size-13 fw-normal'>{info.row.original.email}</h5>
              <p className='m-0 text-muted font-size-10'>{info.row.original.role}</p>
            </div>
          </div>
        ),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'documentName',
        id: 'documentName',
        header: 'File',
        cell: (info) => (
          <div className='d-flex align-items-center gap-2'>
            <img
              src={getFileType(info.row.original.fileName)}
              alt={info.row.original.documentName}
              height={32}
              width={32}
            />
            <div>
              <h5 className='m-0 font-size-13 fw-normal'>{info.row.original.documentName}</h5>
              <div className='d-flex align-items-center'>
                <img src={folderIcon} alt='folder' height={16} width={16} />
                <p className='font-size-10 m-0 text-muted mt-1'>{info.row.original.path ? "..." + info.row.original.path.slice(info.row.original.path.length - 25, info.row.original.path.length) : ''}</p>
              </div>
            </div>
          </div>
        ),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'accessTime',
        id: 'accessTime',
        header: 'Access Date & Time',
        cell: (info) => {
          const rawDate: string = info.getValue()
          const formattedDate = `${getDateValue(rawDate)} ${getTimeZoneValue(rawDate)}`
          return <span>{formattedDate}</span>
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'location',
        id: 'location',
        header: 'Location',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
    ],
    [] // eslint-disable-line
  )

  useEffect(() => {
    // Function to handle clicks outside of the dropdown menu
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  const exportToCsv = async () => {
    if (!companyId) return
    try {
      const {
        data: { data: values, success },
      } = await exportCSVAccessLogs(companyId)
      if (success) {
        toast.success(formatMessage({ id: 'Access logs exported successfully' }))
        const url = window.URL.createObjectURL(new Blob([values]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'access_logs.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <>
      <div className='d-flex justify-content-end mb-4'>
        <div className='d-flex align-items-center gap-2 w-100 w-md-auto'>
          <form className=' align-items-center w-100 w-md-auto d-flex w-100'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                setGlobalFilter(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.AdvisorMentorSearchSuccessful,
                      action: 'normal',
                      // metadata: {
                      //     company_id: companyId,
                      //     user_id: currentUser?.userId,
                      //     email: currentUser?.email,
                      //     search_value: value,
                      // },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 w-100'
              placeholder='Search'
            />
          </form>
          <div className='d-flex align-items-center position-relative'>
            <div className='dropdown-export-container'>
              <Dropdown>
                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                  <img src={threeDots} alt='' className='three-dots-image' />
                </Dropdown.Toggle>

                <Dropdown.Menu className='m-1'>
                  <Dropdown.Item
                    className='text-dark ps-5'
                    onClick={() => {
                      exportToCsv()
                    }}
                  >
                    {formatMessage({ id: 'Export to CSV' })}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!isLoading && (data?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage />
          </div>
        ) : (
          <Table className='w-100' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-13 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-4' style={{ minWidth: '220px' }}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id} className='border-bottom'>
                    {row.getVisibleCells().map((cell, index) => (
                      <>
                        {index === 2
                          ?
                          <td key={cell.id} className='pt-5 border-0' style={{ minWidth: '200px' }}>
                            <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                          </td>
                          :
                          <td key={cell.id} className='pt-5 border-0' style={{ minWidth: '120px' }}>
                            <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                          </td>}
                      </>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom'>
        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-md-0 mb-4'>
          <select
            aria-label='Default select example'
            className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark '
            style={{ outline: 'none' }}
            value={pagination.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value)
              setPagination((prev) => ({
                ...prev,
                pageSize: newSize,
                pageIndex: 0, // Reset to first page when page size changes
              }))
            }}
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>

          <span className='font-size-13 text-center text-md-start'>
            {formatMessage({
              id: `Showing ${pagination.currentPage === 1
                ? 1
                : (pagination.currentPage - 1) * pagination.pageSize + 1
                } to ${pagination.nextPage ? data.length : pagination.totalCount} of ${pagination.totalCount
                } records`,
            })}
          </span>
        </div>

        {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          <Pagination.First
            onClick={() => handlePageChange(0)}
            disabled={pagination.currentPage === 0}
          >
            {'<<'}
          </Pagination.First>

          <Pagination.Prev
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.prevPage}
          >
            {'<'}
          </Pagination.Prev>

          {Array.from({ length: 3 }, (_, i) => {
            const pageNumber = pagination.currentPage + i;
            return (
              pageNumber <= pagination.totalPages && (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === pagination.currentPage}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              )
            );
          })}

          <Pagination.Next
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.nextPage}
          >
            {'>'}
          </Pagination.Next>

          <Pagination.Last
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={!pagination.nextPage}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination> */}
        <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          {/* First Page Button */}
          <Pagination.First
            onClick={() => handlePageChange(0)}
            disabled={pagination.currentPage === 0}
          >
            {'<<'}
          </Pagination.First>

          {/* Previous Page Button */}
          <Pagination.Prev
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {/* Dynamic Page Number Buttons */}
          {Array.from({ length: 3 }, (_, i) => {
            const pageNumber = pagination.currentPage + i;
            return (
              pageNumber <= pagination.totalPages && (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === pagination.currentPage}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              )
            );
          })}

          {/* Next Page Button */}
          <Pagination.Next
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage >= pagination.totalPages - 1}
          >
            {'>'}
          </Pagination.Next>

          {/* Last Page Button */}
          <Pagination.Last
            onClick={() => handlePageChange(pagination.totalPages - 1)}
            disabled={pagination.currentPage >= pagination.totalPages - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination>

      </div>
      <Filter />
    </>
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
